import { supabase } from "../lib/supabase";

function isMissingRecoveryFields(error) {
  if (!error) return false;

  return (
    error.code === "PGRST204" ||
    error.code === "42P01" ||
    /column/i.test(error.message || "")
  );
}

async function resolveBookingCity(booking) {
  if (booking?.city) {
    return booking.city;
  }

  if (!booking?.order_id) {
    return null;
  }

  const { data: order, error: orderError } = await supabase
    .from("orders")
    .select("address_id")
    .eq("id", booking.order_id)
    .single();

  if (orderError || !order?.address_id) {
    return null;
  }

  const { data: address, error: addressError } = await supabase
    .from("addresses")
    .select("city")
    .eq("id", order.address_id)
    .single();

  if (addressError) {
    return null;
  }

  return address?.city || null;
}

async function loadWorkersForCity(bookingCity) {
  const { data: workers, error } = await supabase
    .from("worker_availability")
    .select(`
      worker_id,
      city,
      is_online,
      current_status,
      work_nature
    `)
    .eq("is_online", true)
    .eq("city", bookingCity);

  if (error) throw error;

  const workerIds = (workers || []).map((worker) => worker.worker_id).filter(Boolean);

  if (workerIds.length === 0) {
    return [];
  }

  const { data: metricsRows, error: metricsError } = await supabase
    .from("worker_metrics")
    .select("worker_id, acceptance_rate, completion_rate, cancellation_rate, rating")
    .in("worker_id", workerIds);

  if (metricsError && metricsError.code !== "PGRST204" && metricsError.code !== "42P01") {
    throw metricsError;
  }

  const metricsMap = (metricsRows || []).reduce((acc, row) => {
    acc[row.worker_id] = row;
    return acc;
  }, {});

  return (workers || []).map((worker) => ({
    ...worker,
    worker_metrics: metricsMap[worker.worker_id] ? [metricsMap[worker.worker_id]] : [],
  }));
}

async function loadWorkerRecoveryMap(workerIds = []) {
  if (!workerIds.length) return {};

  const { data, error } = await supabase
    .from("workers")
    .select(
      "profile_id, settlement_risk_tier, recovery_mode_status, max_unsecured_cash_exposure, current_settlement_due, recovery_hold_percent, cash_jobs_allowed, digital_priority_only, settlement_restriction_reason, runaway_risk_score"
    )
    .in("profile_id", workerIds);

  if (error) {
    if (isMissingRecoveryFields(error)) {
      return {};
    }

    throw error;
  }

  return (data || []).reduce((acc, row) => {
    acc[row.profile_id] = row;
    return acc;
  }, {});
}

function getRecoveryPenalty(recovery) {
  if (!recovery) return 0;
  if (recovery.recovery_mode_status === "suspended") return -10000;
  if (recovery.recovery_mode_status === "cash_blocked") return -150;
  if (recovery.recovery_mode_status === "recovery") return -60;
  if (recovery.recovery_mode_status === "watch") return -20;
  return 0;
}

export async function assignWorkerToBooking(booking) {
  try {
    const bookingCity = await resolveBookingCity(booking);

    if (!bookingCity) {
      await supabase
        .from("service_bookings")
        .update({
          assignment_status: "manual_assignment_required",
          status: "awaiting_manual_assignment",
        })
        .eq("id", booking.id);

      return {
        success: false,
        message: "Booking city is missing for worker assignment",
      };
    }

    const workers = await loadWorkersForCity(bookingCity);
    const recoveryMap = await loadWorkerRecoveryMap(
      (workers || []).map((worker) => worker.worker_id).filter(Boolean)
    );

    const eligibleWorkers = (workers || []).filter((worker) => {
      const skills = worker.work_nature || [];
      const serviceCategory = (booking.service_category || "").toLowerCase();
      const matchesSkill = skills.some((skill) =>
        String(skill).toLowerCase().includes(serviceCategory)
      );
      const recovery = recoveryMap[worker.worker_id];
      const blockedByRecovery =
        recovery?.recovery_mode_status === "suspended" ||
        recovery?.cash_jobs_allowed === false;

      return matchesSkill && !blockedByRecovery;
    });

    if (eligibleWorkers.length === 0) {
      await supabase
        .from("service_bookings")
        .update({
          assignment_status: "manual_assignment_required",
          status: "awaiting_manual_assignment",
        })
        .eq("id", booking.id);

      return { success: false, message: "No eligible workers available" };
    }

    eligibleWorkers.sort((a, b) => {
      const aMetrics = a.worker_metrics?.[0] || {};
      const bMetrics = b.worker_metrics?.[0] || {};

      const aRecovery = recoveryMap[a.worker_id];
      const bRecovery = recoveryMap[b.worker_id];

      const aScore =
        (Number(aMetrics.completion_rate || 0) * 0.4) +
        (Number(aMetrics.acceptance_rate || 0) * 0.3) +
        (Number(aMetrics.rating || 0) * 10 * 0.2) -
        (Number(aMetrics.cancellation_rate || 0) * 0.5) +
        getRecoveryPenalty(aRecovery);

      const bScore =
        (Number(bMetrics.completion_rate || 0) * 0.4) +
        (Number(bMetrics.acceptance_rate || 0) * 0.3) +
        (Number(bMetrics.rating || 0) * 10 * 0.2) -
        (Number(bMetrics.cancellation_rate || 0) * 0.5) +
        getRecoveryPenalty(bRecovery);

      return bScore - aScore;
    });

    const selectedWorker = eligibleWorkers[0];

    const { error: updateError } = await supabase
      .from("service_bookings")
      .update({
        assigned_worker_id: selectedWorker.worker_id,
        assignment_status: "assigned",
        status: "worker_assigned",
        assigned_at: new Date().toISOString(),
      })
      .eq("id", booking.id);

    if (updateError) throw updateError;

    await supabase.rpc("recalc_worker_metrics", {
      p_worker_id: selectedWorker.worker_id,
    });

    return { success: true, workerId: selectedWorker.worker_id };
  } catch (error) {
    console.error("assignWorkerToBooking error:", error);
    return { success: false, message: error.message };
  }
}

export async function reassignWorkerAfterCancellation(bookingId, oldWorkerId) {
  try {
    const { data: booking, error: bookingError } = await supabase
      .from("service_bookings")
      .select("*")
      .eq("id", bookingId)
      .single();

    if (bookingError) throw bookingError;

    const bookingCity = await resolveBookingCity(booking);

    if (!bookingCity) {
      await supabase
        .from("service_bookings")
        .update({
          assignment_status: "reassignment_failed",
          status: "awaiting_manual_assignment",
        })
        .eq("id", bookingId);

      return { success: false, message: "Booking city is missing for reassignment" };
    }

    const workers = await loadWorkersForCity(bookingCity);
    const recoveryMap = await loadWorkerRecoveryMap(
      (workers || []).map((worker) => worker.worker_id).filter(Boolean)
    );

    const eligibleWorkers = (workers || []).filter((worker) => {
      if (worker.worker_id === oldWorkerId) return false;
      const skills = worker.work_nature || [];
      const serviceCategory = (booking.service_category || "").toLowerCase();
      const matchesSkill = skills.some((skill) =>
        String(skill).toLowerCase().includes(serviceCategory)
      );
      const recovery = recoveryMap[worker.worker_id];
      const blockedByRecovery =
        recovery?.recovery_mode_status === "suspended" ||
        recovery?.cash_jobs_allowed === false;

      return matchesSkill && !blockedByRecovery;
    });

    if (eligibleWorkers.length === 0) {
      await supabase
        .from("service_bookings")
        .update({
          assignment_status: "reassignment_failed",
          status: "awaiting_manual_assignment",
        })
        .eq("id", bookingId);

      return { success: false, message: "No replacement worker found" };
    }

    eligibleWorkers.sort((a, b) => {
      const aMetrics = a.worker_metrics?.[0] || {};
      const bMetrics = b.worker_metrics?.[0] || {};

      const aRecovery = recoveryMap[a.worker_id];
      const bRecovery = recoveryMap[b.worker_id];

      const aScore =
        (Number(aMetrics.completion_rate || 0) * 0.4) +
        (Number(aMetrics.acceptance_rate || 0) * 0.3) +
        (Number(aMetrics.rating || 0) * 10 * 0.2) -
        (Number(aMetrics.cancellation_rate || 0) * 0.5) +
        getRecoveryPenalty(aRecovery);

      const bScore =
        (Number(bMetrics.completion_rate || 0) * 0.4) +
        (Number(bMetrics.acceptance_rate || 0) * 0.3) +
        (Number(bMetrics.rating || 0) * 10 * 0.2) -
        (Number(bMetrics.cancellation_rate || 0) * 0.5) +
        getRecoveryPenalty(bRecovery);

      return bScore - aScore;
    });

    const replacementWorker = eligibleWorkers[0];

    const { error: updateError } = await supabase
      .from("service_bookings")
      .update({
        assigned_worker_id: replacementWorker.worker_id,
        assignment_status: "reassigned",
        status: "worker_reassigned",
        reassignment_count: Number(booking.reassignment_count || 0) + 1,
        assigned_at: new Date().toISOString(),
      })
      .eq("id", bookingId);

    if (updateError) throw updateError;

    await supabase.rpc("recalc_worker_metrics", {
      p_worker_id: oldWorkerId,
    });

    await supabase.rpc("recalc_worker_metrics", {
      p_worker_id: replacementWorker.worker_id,
    });

    return { success: true, workerId: replacementWorker.worker_id };
  } catch (error) {
    console.error("reassignWorkerAfterCancellation error:", error);
    return { success: false, message: error.message };
  }
}
